import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd, mediaQueryLarge } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** @type {AbortController | undefined} */
  #flyoutController;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #flyoutCloseTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupDesktopFlyouts();
    mediaQueryLarge.addEventListener('change', this.#onBreakpointChange);

    const { details } = this.refs;
    if (details.hasAttribute('open')) {
      details.classList.add('menu-open');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange);
    this.#clearFlyoutCloseTimeout();
    this.#flyoutController?.abort();
  }

  #onBreakpointChange = () => {
    this.#setupDesktopFlyouts();
    if (!mediaQueryLarge.matches) {
      this.#clearFlyoutCloseTimeout();
      this.#closeDesktopFlyouts();
    }
  };

  #clearFlyoutCloseTimeout() {
    if (this.#flyoutCloseTimeout !== undefined) {
      clearTimeout(this.#flyoutCloseTimeout);
      this.#flyoutCloseTimeout = undefined;
    }
  }

  /**
   * Hover flyouts for desktop hamburger drawer (panel beside the main drawer).
   */
  #setupDesktopFlyouts() {
    this.#flyoutController?.abort();
    this.#clearFlyoutCloseTimeout();

    if (!this.#isDesktopFlyoutEnabled()) return;

    this.#flyoutController = new AbortController();
    const { signal } = this.#flyoutController;

    const items = this.querySelectorAll('.menu-drawer__list-item--deep');

    for (const item of items) {
      if (!(item instanceof HTMLElement)) continue;

      item.addEventListener('pointerenter', this.#onFlyoutEnter, { signal });
      item.addEventListener('pointerleave', this.#onFlyoutLeave, { signal });
      item.addEventListener('focusin', this.#onFlyoutEnter, { signal });

      const flyout = item.querySelector('.menu-drawer__flyout');
      if (flyout instanceof HTMLElement) {
        flyout.addEventListener('pointerenter', this.#onFlyoutEnter, { signal });
        flyout.addEventListener('pointerleave', this.#onFlyoutLeave, { signal });
      }
    }

    const closeButton = this.querySelector('.menu-drawer__close-button--desktop');
    if (closeButton instanceof HTMLElement) {
      closeButton.addEventListener('pointerenter', this.#onCloseButtonEnter, { signal });
      closeButton.addEventListener('pointerleave', this.#onCloseButtonLeave, { signal });
    }
  }

  #isDesktopFlyoutEnabled() {
    return (
      mediaQueryLarge.matches &&
      Boolean(this.closest('.header__drawer--desktop, .header--drawer-desktop'))
    );
  }

  /**
   * @param {Event} event
   */
  #onFlyoutEnter = (event) => {
    if (!this.#isDesktopFlyoutEnabled()) return;

    this.#clearFlyoutCloseTimeout();

    const item = this.#getFlyoutItem(event);
    if (!item) return;

    const details = item.querySelector('accordion-custom > details');
    if (!(details instanceof HTMLDetailsElement)) return;

    this.#closeDesktopFlyouts(details);
    details.setAttribute('open', '');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
  };

  /**
   * @param {Event} event
   */
  #onFlyoutLeave = (event) => {
    if (!this.#isDesktopFlyoutEnabled()) return;

    const item = this.#getFlyoutItem(event);
    if (!item) return;

    const related = event instanceof PointerEvent ? event.relatedTarget : null;
    if (related instanceof Node && item.contains(related)) return;
    if (this.#isCloseButtonTarget(related)) return;

    this.#scheduleFlyoutClose(item);
  };

  #onCloseButtonEnter = () => {
    if (!this.#isDesktopFlyoutEnabled()) return;
    this.#clearFlyoutCloseTimeout();
  };

  /**
   * @param {Event} event
   */
  #onCloseButtonLeave = (event) => {
    if (!this.#isDesktopFlyoutEnabled()) return;

    const related = event instanceof PointerEvent ? event.relatedTarget : null;
    if (related instanceof Element) {
      const item = related.closest('.menu-drawer__list-item--deep');
      if (item?.querySelector('accordion-custom > details[open]')) return;
    }

    const openItem = this.querySelector(
      '.menu-drawer__list-item--deep:has(accordion-custom > details[open])'
    );
    if (openItem instanceof HTMLElement) {
      this.#scheduleFlyoutClose(openItem);
    }
  };

  /**
   * @param {EventTarget | null} target
   * @returns {boolean}
   */
  #isCloseButtonTarget(target) {
    return (
      target instanceof Element &&
      Boolean(target.closest('.menu-drawer__close-button--desktop'))
    );
  }

  /**
   * @param {HTMLElement} item
   */
  #scheduleFlyoutClose(item) {
    const details = item.querySelector('accordion-custom > details');
    if (!(details instanceof HTMLDetailsElement)) return;

    this.#clearFlyoutCloseTimeout();
    this.#flyoutCloseTimeout = setTimeout(() => {
      const closeButton = this.querySelector('.menu-drawer__close-button--desktop');
      if (
        item.matches(':hover') ||
        item.contains(document.activeElement) ||
        (closeButton instanceof HTMLElement && closeButton.matches(':hover'))
      ) {
        return;
      }

      details.removeAttribute('open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    }, 150);
  }

  /**
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #getFlyoutItem(event) {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return null;

    if (target.classList.contains('menu-drawer__list-item--deep')) return target;

    return target.closest('.menu-drawer__list-item--deep');
  }

  /**
   * @param {HTMLDetailsElement} [except]
   */
  #closeDesktopFlyouts(except) {
    const openDetails = this.querySelectorAll('.menu-drawer__list-item--deep accordion-custom > details[open]');

    for (const details of openDetails) {
      if (!(details instanceof HTMLDetailsElement)) continue;
      if (except && details === except) continue;

      details.removeAttribute('open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    if (event instanceof Event) {
      event.preventDefault();
    }

    return this.isOpen ? this.close() : this.open(event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    details.setAttribute('open', '');
    details.classList.add('menu-open');

    this.preventInitialAccordionAnimations(details);

    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
    onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the drawer immediately, then open the predictive search modal (mobile app nav).
   * Immediate reset avoids nested scroll-lock (html + dialog) that blocks modal scrolling.
   */
  openSearch() {
    const { details } = this.refs;

    removeTrapFocus();
    this.#closeDesktopFlyouts();
    details.classList.remove('menu-open');
    reset(details);
    document.documentElement.removeAttribute('scroll-lock');

    const searchModal = document.getElementById('search-modal');
    if (!(searchModal instanceof HTMLElement) || typeof searchModal.showDialog !== 'function') return;

    requestAnimationFrame(() => {
      searchModal.showDialog();
    });
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.#closeDesktopFlyouts();

    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
          this.#closeDesktopFlyouts();
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
